import styles from './ProfileSettings.module.scss'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Button } from '../../components/Button/Button'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetUserByIdQuery, useSaveUserByIdMutation } from '../../store/user/user'
import { Divider } from '../../components/Divider/Divider'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { TagElement } from '../../components/Tag/Tag'
import { Roles } from '../../components/Roles/Roles'
import { getUserName } from '../utils'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { EmailSignature } from './EmailSignature/EmailSignature'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { USER_PRONOUNS, UserPronouns } from '../../models/User'
import { UserTimezoneSettings } from './Timezone/UserTimezoneSettings'
import { SuccessToast } from '../../components/SuccessToast/SuccessToast'
export const ProfileSettingsTab: React.FC = () => {
  return (
    <Container className='p-0'>
      <Row className='mb-8'>
        <Col lg={7}>
          <ProfileSettingsForm />
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col>
          <UserTimezoneSettings />
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col>
          <EmailSignature />
        </Col>
      </Row>
      {/*<Row className='mb-8 mt-5'>*/}
      {/*  <Col>*/}
      {/*    <PasswordChangeForm />*/}
      {/*  </Col>*/}
      {/*</Row>*/}
      <Divider />
      <Row className='mt-5'>
        <Col>
          <DeleteMyAccountForm />
        </Col>
      </Row>
    </Container>
  )
}

const ProfileSettingsForm: React.FC = () => {
  const pronounOptions: SelectOption[] = USER_PRONOUNS.map((pronoun) => ({ id: pronoun, label: pronoun }))

  type FormValues = {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    roles: string[]
    status: string
    preferredPronouns: UserPronouns | null
  }
  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roles: [],
    status: 'active',
    preferredPronouns: null,
  }

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)

  const { data: userData, refetch } = useGetUserByIdQuery({ accountId: accountId, userId: currentUser!.id }, { refetchOnMountOrArgChange: true })

  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveUserByIdMutation()

  const { control, setValue, handleSubmit } = useForm<FormValues>({ defaultValues })
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false)

  const resetForm = useCallback(() => {
    if (userData) {
      setValue('firstName', userData.data.firstName ?? '')
      setValue('lastName', userData.data.lastName ?? '')
      setValue('email', userData.data.email ?? '')
      setValue('preferredPronouns', userData.data.settings?.preferredPronouns ?? null)
      //setValue('phoneNumber', userData.data.phoneNumber ?? '')
      setValue('roles', userData.data.roles.map((role) => role.id) ?? [])
      setValue('status', userData.data.status ?? 'active')
    }
  }, [setValue, userData])

  useEffect(() => {
    resetForm()
  }, [resetForm, userData])

  const onSubmit = async (data: FormValues) => {
    if (currentUser) {
      try {
        await saveUserById({
          accountId,
          userId: currentUser.id,
          keepEmptyItems: true,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            name: getUserName(data.firstName, data.lastName) || undefined,
            email: data.email,
            //phoneNumber: data.phoneNumber,
            settings: {
              preferredPronouns: data.preferredPronouns,
            },
          },
        })
        setShowSuccessToast(true)
      } catch (err) {
        console.error(err)
      }

      // TODO: If role update needed.
      // updateUserRoles({
      //   accountId,
      //   userId: currentUser.id,
      //   roleIds: data.roles,
      // })
    }
  }

  useEffect(() => {
    if (isSuccess) {
      refetch()
    }
  }, [isSuccess, refetch])

  return (
    <>
      <Container className='p-0'>
        <SuccessToast message={'Personal information updated'} show={showSuccessToast} onClose={() => setShowSuccessToast(false)} delay={3000} />
        <Row className='zocks-mb-2.5'>
          <Col>
            <h5>Personal Information</h5>
          </Col>
        </Row>
      </Container>
      <Container className='d-flex flex-column gap-4 p-0'>
        <Row>
          <Col>
            <div className='label-medium'>User Avatar</div>
          </Col>
          <Col>
            <UserAvatar className={styles.userAvatar} />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='label-medium'>Email address</div>
          </Col>
          <Col>
            <Controller
              name='email'
              control={control}
              rules={{
                required: 'Email address is required.',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Entered value does not match email format',
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Control id={field.name} disabled={true} type='email' placeholder='Enter your email address' {...field} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='d-flex zocks-gap-0.125 align-items-center'>
              <div className='label-medium'>Pronoun</div>
              <Tooltip
                className='zocks-text-gray-500'
                placement='right'
                title='Zocks will use this pronoun when referring to you in meeting summaries,  prep notes, and templates.'
              >
                <i className='bi bi-info-circle ms-1' />
              </Tooltip>
            </div>
          </Col>
          <Col>
            <Controller
              name='preferredPronouns'
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <SelectDropdown
                    options={pronounOptions}
                    initialSelectedOption={field.value ? { id: field.value, label: field.value } : null}
                    onOptionSelected={(option) => field.onChange(option.id as UserPronouns)}
                    placeholder='Not set'
                    showDeselectOption
                    deselectOptionLabel='Not set'
                    onDeselect={() => field.onChange(null)}
                    className='py-2 zocks-px-0.75 zocks-border-color-gray-500'
                  />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='label-medium'>First name</div>
          </Col>
          <Col>
            <Controller
              name='firstName'
              control={control}
              render={({ field }) => (
                <>
                  <Form.Control id={field.name} type='text' placeholder='Enter your First name' {...field} />
                </>
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='label-medium'>Last name</div>
          </Col>
          <Col>
            <Controller
              name='lastName'
              control={control}
              render={({ field }) => (
                <>
                  <Form.Control id={field.name} type='text' placeholder='Enter your Last name' {...field} />
                </>
              )}
            />
          </Col>
        </Row>
        {/*<Row>*/}
        {/*  <Col>*/}
        {/*    <div className='label-medium'>Phone number</div>*/}
        {/*  </Col>*/}
        {/*  <Col>*/}
        {/*    <Controller*/}
        {/*      name='phoneNumber'*/}
        {/*      control={control}*/}
        {/*      rules={{*/}
        {/*        pattern: { value: /^\+[1-9][0-9]{0,24}$/i, message: 'Invalid phone number. E.g. +11234567890' },*/}
        {/*      }}*/}
        {/*      render={({ field, fieldState }) => (*/}
        {/*        <>*/}
        {/*          <Form.Control id={field.name} type='text' placeholder='Enter your Phone number' {...field} />*/}
        {/*        </>*/}
        {/*      )}*/}
        {/*    />*/}
        {/*  </Col>*/}
        {/*</Row>*/}
        <Row>
          <Col>
            <div className='label-medium'>Role</div>
          </Col>
          <Col>
            {/*<Controller*/}
            {/*  name='roles'*/}
            {/*  control={control}*/}
            {/*  render={({ field, fieldState }) => (*/}
            {/*    <>*/}
            {/*      <MultiSelectDropdown*/}
            {/*        register={register}*/}
            {/*        name='roles'*/}
            {/*        options={*/}
            {/*          roleData?.data.map((role) => {*/}
            {/*            return { id: role.id, label: role.name }*/}
            {/*          }) || []*/}
            {/*        }*/}
            {/*        initialSelectedOptions={field.value}*/}
            {/*      />*/}
            {/*    </>*/}
            {/*  )}*/}
            {/*/>*/}
            <div className='d-flex gap-2'>
              <Roles userRoleIds={userData ? userData.data.roles.map((role) => role.id) : []} roleElement={TagElement} />
            </div>
            {/*{userData ? userData.data.roles.map((role) => <TagElement>{role.name}</TagElement>) : null}</div>*/}
          </Col>
        </Row>
        <Row className='d-flex gap-3 justify-content-end'>
          <Col className='p-0' sm='auto'>
            <Button variant='secondary' onClick={() => resetForm()}>
              Cancel
            </Button>
          </Col>
          <Col className='p-0' sm='auto'>
            <Button variant='primary' onClick={handleSubmit(onSubmit)} loading={isUpdating}>
              Save
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  )
}

/*
const PasswordChangeForm: React.FC = () => {
  return (
    <Container className='p-0'>
      <Row className='zocks-mb-2.5' sm={12}>
        <Col>
          <h5>Password</h5>
        </Col>
      </Row>
      <Row className='zocks-mb-2'>
        <Col sm={12}>
          Note that if you signed in with a connected account, you are using that account’s login information and we cannot change or reset those passwords
          here.
        </Col>
      </Row>
      <Row>
        <Col lg={7}>
          <div className='d-flex flex-column gap-3'>
            <Row>
              <Col>
                <div className='label-medium'>Password</div>
              </Col>
              <Col>
                <PasswordInput />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className='label-medium'>Reset Password</div>
              </Col>
              <Col>
                <Row className='zocks-mb-1'>
                  <div className='paragraph-medium'>If you just forgot your password, don’t worry – we got you!</div>
                </Row>
                <Row>
                  <Button variant='link'>
                    <i className='bi bi-lock' />
                    Reset Password
                  </Button>
                </Row>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
*/

const DeleteMyAccountForm: React.FC = () => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)
  const mailToSubject = `Delete My Account - ${accountId}`
  const mailToBody = `Dear Zocks,
I am writing to request the deletion of my account on Zocks.

My account details are as follows:
- Account Id: ${accountId}
- My Email Address: ${currentUser?.email}

Please confirm the account deletion and let me know if there are any further steps required from my side to complete this process.

Thank you for your assistance in this matter.
`
  const openSystemEmailClient = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      window.location.href = `mailto:support@zocks.io?subject=${encodeURIComponent(mailToSubject)}&body=${encodeURIComponent(mailToBody)}`
      e.preventDefault()
    },
    [mailToBody, mailToSubject],
  )
  const handleDeleteMyAccount = tracked(openSystemEmailClient, { eventName: TrackEvent.DELETE_MY_ACCOUNT_CLICKED })

  return (
    <Container className='p-0'>
      <ScopedBlock
        scope={{
          scope: 'billing_subscription:change',
          superScope: ['billing_subscription:admin'],
        }}
        missingScopeAction={MissingScopeAction.HIDE}
      >
        <Row className='zocks-mb-2.5'>
          <Col sm='auto'>
            <h5>Delete my account</h5>
          </Col>
        </Row>
        {/*<Row className='zocks-mb-2'>*/}
        {/*  <Col sm='auto'>*/}
        {/*    <div className='paragraph-medium'>Do you want to downgrade instead?</div>*/}
        {/*  </Col>*/}
        {/*  <Col sm='auto'>*/}
        {/*    <Button variant='link' onClick={() => navigate('/account/plan/plan')}>*/}
        {/*      Manage Subscriptions*/}
        {/*    </Button>*/}
        {/*  </Col>*/}
        {/*</Row>*/}
        <Row>
          <Col>
            <Button className='mb-5' variant='danger' onClick={handleDeleteMyAccount}>
              Delete my account
            </Button>
          </Col>
        </Row>
      </ScopedBlock>
    </Container>
  )
}
